import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import type { MouseEvent } from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import DecimalInput from '@/components/inputs/DecimalInput'
import type { VolumeCapacityConfig, VolumeCapacityMode } from '@/features/bidding/types/bidLadderInput'

type VolumeCapacityInputProps = {
  config: VolumeCapacityConfig
  onChange: (config: VolumeCapacityConfig) => void
  /** Number of decimal places allowed when mode is 'fixed' (e.g. 1 for FCR, 0 for mFRR) */
  fixedModeDecimalPlaces?: number
}

function VolumeCapacityInput({ config, onChange, fixedModeDecimalPlaces = 1 }: Readonly<VolumeCapacityInputProps>) {
  const { t } = useTranslation()

  const handleValueChange = useCallback(
    (value: number | null) => {
      onChange({ ...config, value })
    },
    [config, onChange],
  )

  const handleModeChange = useCallback(
    (_event: MouseEvent<HTMLElement>, newMode: VolumeCapacityMode | null) => {
      if (newMode !== null) {
        const isNewModePercentage = newMode !== 'fixed'
        const value = isNewModePercentage && config.value !== null ? Math.floor(config.value) : config.value
        onChange({ ...config, mode: newMode, value })
      }
    },
    [config, onChange],
  )

  const isFixedMode = config.mode === 'fixed'
  const fixedPlaceholder = fixedModeDecimalPlaces === 0 ? '0' : '0.0'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        flex: 1,
        minWidth: 0,
      }}
    >
      <DecimalInput
        selectOnFocus
        decimalPlaces={isFixedMode ? fixedModeDecimalPlaces : 0}
        min={0}
        placeholder={isFixedMode ? fixedPlaceholder : '0'}
        size="small"
        slotProps={{
          htmlInput: {
            'aria-label': t('bidding.bidding_cascading.bid_ladder.fcr_capacity_value'),
          },
        }}
        sx={{
          width: 64,
          flexShrink: 0,
          '& .MuiOutlinedInput-root': {
            height: 28,
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '0.75rem',
            padding: '4px 8px',
            textAlign: 'right',
          },
        }}
        value={config.value}
        onValueChange={handleValueChange}
      />
      <ToggleButtonGroup
        exclusive
        size="small"
        sx={{
          height: 28,
          '& .MuiToggleButton-root': {
            height: 28,
            padding: '2px 8px',
            fontSize: '0.625rem',
            textTransform: 'none',
            lineHeight: 1.2,
          },
        }}
        value={config.mode}
        onChange={handleModeChange}
      >
        <ToggleButton value="fixed">{t('bidding.bidding_cascading.bid_ladder.fcr_mode_fixed')}</ToggleButton>
        <ToggleButton value="percent_of_suggested">
          {t('bidding.bidding_cascading.bid_ladder.fcr_mode_percent_suggested')}
        </ToggleButton>
        <ToggleButton value="percent_of_available">
          {t('bidding.bidding_cascading.bid_ladder.fcr_mode_percent_available')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}

export default VolumeCapacityInput
